import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { BlazeSchema, ColumnType, DatasetColumn } from '@blaze-shared/schema-domain';

import { TableInlineEditorUtilsService } from '../../../../services/table-inline-editor-utils.service';
import {
  isColumnValueInlineEditable,
  MenuEditModeColumnFormats,
  MenuEditModeDataTypes,
} from '../../../../utils/elements/table-inline-editor.utils';
import { stripStyleTags } from '../../../../utils/elements/tiny-mce.utils';
import { TableDateTimeEditorComponent } from '../datetime/datetime-editor.component';
import { TableLinkMenuComponent } from '../link-menu/link-menu.component';
import { TableParagraphEditorComponent } from '../paragraph/paragraph-editor.component';

import {
  type CellEditMode,
  CellValueUpdateModel,
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
  TextInputFormat,
} from './inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-base-editor',
  templateUrl: './base-editor.component.html',
  styleUrls: ['./base-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableBaseEditorComponent implements OnDestroy {
  private tableInlineEditorUtilsService = inject(TableInlineEditorUtilsService);
  private dialog = inject(MatDialog);
  elementRef = inject(ElementRef);

  @Input() config?: InlineEditorConfig;
  @Input() editMode?: CellEditMode;
  @Input() layoutMode?: InlineEditorLayoutMode = 'always-active';
  @Output() focusLost = new EventEmitter();
  @ViewChild('stringInput') stringInput!: ElementRef<HTMLInputElement>;
  @ViewChild('datetimeEditor') datetimeEditor?: TableDateTimeEditorComponent;
  @ViewChild('autosize') autosize?: CdkTextareaAutosize;
  @ViewChild('linkMenu') linkMenu?: TableLinkMenuComponent;

  private paragraphDialog?: MatDialogRef<TableParagraphEditorComponent>;

  value?: string | number | null;
  savedErrorValue?: any;
  valueFormat?: TextInputFormat;
  formControl = new UntypedFormControl('');
  menuSwitchValue: string | boolean | undefined;

  @HostBinding('class.type-datetime') get isDateTime(): boolean {
    return this.config?.columnSchema?.columnType === 'DateTime';
  }
  @HostBinding('class.format-paragraph') get isParagraph(): boolean {
    return this.config?.columnSchema?.format?.formatType === 'Paragraph';
  }
  @HostBinding('class.input-like') get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }
  @HostBinding('class.px-10') get shouldAddXPadding(): boolean {
    return (
      this.isInputLike && ['File', 'Image', 'Boolean', 'Link', 'Tag'].includes(this.dataType ?? '')
    );
  }

  get columnType(): ColumnType | undefined {
    return this.config?.columnSchema?.columnType;
  }

  get dataType(): ColumnDataType | undefined {
    return this.config?.columnSchema?.dataType;
  }

  get formControlValue(): any {
    return typeof this.formControl.value === 'string'
      ? this.formControl.value.trim()
      : this.formControl.value;
  }

  @HostBinding('class.inline-editor') get isInlineEditor(): boolean {
    return this.editMode === 'inline-editor';
  }

  @HostBinding('class.input-like') get isInputLikeEditor(): boolean {
    return this.layoutMode === 'input-like';
  }

  constructor() {
    const tableInlineEditorUtilsService = this.tableInlineEditorUtilsService;

    tableInlineEditorUtilsService.closeInlineEditor.pipe(untilDestroyed(this)).subscribe(() => {
      // fix for UI Table action buttons that prevent default action
      this.focusOut(undefined, undefined, true);
    });
  }

  ngOnDestroy(): void {
    this.paragraphDialog?.close();
  }

  @HostListener('focusout', ['$event'])
  focusOut(event?: FocusEvent, omitValue = false, force = false): void {
    if (this.layoutMode === 'input-like' && !this.isInlineEditor) {
      return;
    }
    if (!force) {
      if (
        ['Date', 'DateTime', 'Time'].includes(this.columnType || '') &&
        event?.relatedTarget &&
        ((event.relatedTarget as HTMLElement).closest('mat-datepicker-content') ||
          (event?.relatedTarget as HTMLElement)?.closest('.inline-editor-time-picker'))
      ) {
        return;
      } else if (
        event?.relatedTarget &&
        this.elementRef.nativeElement.contains(event.relatedTarget) &&
        isColumnValueInlineEditable(this.config?.columnSchema)
      ) {
        return;
      } else if (
        this.columnType === 'String' &&
        this.valueFormat === 'Paragraph' &&
        // dialog editor click
        ((event?.relatedTarget &&
          (event.relatedTarget as HTMLElement).closest('.inline-editor-paragraph-editor')) ||
          // textarea click
          (event?.target && (event.target as HTMLElement).tagName === 'TEXTAREA'))
      ) {
        return;
      }
    }

    this.focusLost.emit(omitValue ? undefined : this.formControlValue);
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent): void {
    if (this.layoutMode === 'input-like') {
      return;
    }
    const target = event.target as HTMLElement | undefined;
    const relatedTarget = event.relatedTarget as HTMLElement | undefined;
    if (
      ['Date', 'DateTime'].includes(this.columnType || '') &&
      // if clicked datepicker
      (document.activeElement?.closest('mat-datepicker-content') ||
        target?.closest('mat-datepicker-content') ||
        // if clicked backdrop but datepicker opened
        (this.datetimeEditor?.isDatePickerOpened &&
          target?.classList.contains('cdk-overlay-backdrop')) ||
        // if clicked target is this el child
        relatedTarget?.closest(this.elementRef.nativeElement.tagName) ||
        // if clicked target is month/year picker
        target?.closest('.datepicker-select') ||
        (target?.className?.indexOf('mat-datepicker') ?? -1) > -1)
    ) {
      return;
    } else if (this.columnType === 'Link' && target?.tagName.toLowerCase() === 'input') {
      event.preventDefault();
      event.stopImmediatePropagation();
    } else if (
      this.columnType === 'String' &&
      this.valueFormat === 'Paragraph' &&
      ((event?.target &&
        (event.target as HTMLElement).closest('.inline-editor-paragraph-editor')) ||
        (event?.target && (event.target as HTMLElement).tagName === 'TEXTAREA'))
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    } else if (
      target &&
      !this.elementRef.nativeElement.contains(target) &&
      isColumnValueInlineEditable(this.config?.columnSchema)
    ) {
      this.focusOut();
    } else if (target && !this.elementRef.nativeElement.contains(target)) {
      this.focusOut(event, true);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.layoutMode === 'input-like') {
      return;
    }
    if (event.key === 'Escape') {
      this.focusOut(undefined, true);
    } else if (
      ((event.key === 'Enter' && !event.shiftKey) || event.key === 'Tab') &&
      isColumnValueInlineEditable(this.config?.columnSchema)
    ) {
      this.focusOut();
    }
  }

  setInitialConfig(
    value: string | number | null,
    savedErrorValue: any,
    options: {
      textAlign?: string | undefined;
      columnSchema?: DatasetColumn | undefined;
      detailData?: CellValueUpdateModel['detailData'];
      schema?: BlazeSchema;
      columnLabel?: string;
    } = {},
  ): void {
    this.config = {
      inputStyles: {
        'text-align': options.textAlign || 'left',
      },
      columnSchema: options.columnSchema,
      detailData: options.detailData,
      schema: options.schema,
      columnLabel: options.columnLabel,
    };

    const format = this.config.columnSchema?.format?.formatType;
    this.valueFormat = format ?? this.config.columnSchema?.dataType;
    this.editMode =
      (this.dataType && MenuEditModeDataTypes.includes(this.dataType)) ||
      (format && MenuEditModeColumnFormats.includes(format))
        ? 'menu'
        : 'inline-editor';

    this.menuSwitchValue =
      this.dataType && MenuEditModeDataTypes.includes(this.dataType)
        ? this.dataType
        : format && MenuEditModeColumnFormats.includes(format) && format;

    this.savedErrorValue = savedErrorValue;
    this.value = value;
    this.formControl.setValue(savedErrorValue ?? value);
    this.actionsAfterInit();
  }

  actionsAfterInit(): void {
    setTimeout(() => {
      this.stringInput?.nativeElement?.focus();
      this.stringInput?.nativeElement?.select();
      if (this.isParagraph) {
        this.autosize?.resizeToFitContent(true);
      }
    });
  }

  patchValue(value: any): void {
    if (typeof value === 'string') {
      this.formControl.patchValue(value.trim());
    } else {
      this.formControl.patchValue(value);
    }
    if (this.layoutMode === 'input-like') {
      this.focusLost.emit(value);
    }
  }

  emitFileValue(value: any): void {
    this.focusLost.emit(value);
    if (this.isInputLike) {
      this.formControl.patchValue(value, { emitEvent: false });
    }
  }

  openParagraph(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.paragraphDialog = this.dialog.open(TableParagraphEditorComponent, {
      width: '50%',
      height: 'auto',
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog', 'inline-editor-paragraph-editor'],
      maxWidth: '440px',
      data: { value: this.formControl.value, columnLabel: this.config?.columnLabel },
    });

    this.paragraphDialog
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((val: string) => {
        this.emitFileValue(val);
      });
  }

  pasteClipboard(event: ClipboardEvent): void {
    if (!event.clipboardData) {
      return;
    }
    event.preventDefault();
    const content = stripStyleTags(event.clipboardData.getData('text/plain'));
    pasteInputValue(this.stringInput.nativeElement, content);
  }
}

function pasteInputValue(input: HTMLInputElement, content: string): void {
  const { selectionStart, selectionEnd } = input;
  const currentValue = input.value;
  const updated =
    currentValue.slice(0, selectionStart ?? 0) + content + currentValue.slice(selectionEnd ?? 0);
  input.value = updated;
  input.selectionStart = input.selectionEnd = (selectionStart ?? 0) + content.length;
  input.dispatchEvent(new Event('input', { bubbles: true }));
}
